import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import UserMenuActions from "./UserMenuActions";

/**
 * @param {"header" | "sidebar"} variant
 */
const UserMenu = ({ variant = "header" }) => {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const panelId = useId();
  const isSidebar = variant === "sidebar";

  const displayLabel = useMemo(() => {
    if (!user) {
      return "";
    }

    return user.displayName || user.email;
  }, [user]);

  const initials = useMemo(() => {
    const source = user?.displayName || user?.email || "?";
    const parts = source.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return source.slice(0, 2).toUpperCase();
  }, [user]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!isAuthenticated || !user) {
    if (isSidebar) {
      return (
        <div className="auth-user-menu auth-user-menu--sidebar auth-user-menu--signed-out">
          <Link to="/login" className="auth-user-menu__sidebar-sign-in">
            Sign in
          </Link>
        </div>
      );
    }

    return (
      <Link to="/login" className="midpoint-share-button auth-user-menu__sign-in">
        Sign in
      </Link>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`auth-user-menu${isSidebar ? " auth-user-menu--sidebar" : ""}${
        open ? " is-open" : ""
      }`}
    >
      <button
        type="button"
        className={
          isSidebar
            ? "auth-user-menu__sidebar-trigger"
            : "auth-user-menu__trigger midpoint-share-button"
        }
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={panelId}
      >
        {isSidebar ? (
          <>
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="auth-user-menu__sidebar-avatar auth-user-menu__sidebar-avatar--image"
                width={32}
                height={32}
              />
            ) : (
              <span className="auth-user-menu__sidebar-avatar" aria-hidden="true">
                {initials}
              </span>
            )}
            <span className="auth-user-menu__sidebar-text">
              <span className="auth-user-menu__sidebar-name">{displayLabel}</span>
            </span>
            <span className="auth-user-menu__chevron" aria-hidden="true">
              {open ? "▴" : "▾"}
            </span>
          </>
        ) : (
          <>
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt=""
                className="auth-user-menu__avatar"
                width={28}
                height={28}
              />
            ) : (
              <span className="auth-user-menu__avatar auth-user-menu__avatar--initials" aria-hidden="true">
                {initials}
              </span>
            )}
            <span className="auth-user-menu__header-text">
              <span className="auth-user-menu__name">{displayLabel}</span>
              {user.roles?.[0]?.roleName ? (
                <span className="auth-user-menu__role">{user.roles[0].roleName}</span>
              ) : null}
            </span>
            <span className="auth-user-menu__chevron" aria-hidden="true">
              {open ? "▴" : "▾"}
            </span>
          </>
        )}
      </button>
      {open ? (
        <div
          id={panelId}
          className={`auth-user-menu__panel${
            isSidebar ? " auth-user-menu__panel--sidebar" : ""
          }`}
          role="menu"
        >
          <UserMenuActions onNavigate={() => setOpen(false)} />
        </div>
      ) : null}
    </div>
  );
};

export default UserMenu;
